/**
 * DenominationCounter — count the drawer note by note: type how many of
 * each note and coin, the total fills in. Sets for common African
 * currencies; anything else falls back to typing the total.
 */
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { getCurrencyCode, formatMoney } from "@/lib/currency";

const SETS: Record<string, number[]> = {
  GHS: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1],
  NGN: [1000, 500, 200, 100, 50, 20, 10, 5],
  KES: [1000, 500, 200, 100, 50, 20, 10, 5, 1],
  UGX: [50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100],
  TZS: [10000, 5000, 2000, 1000, 500, 200, 100],
  RWF: [5000, 2000, 1000, 500, 100, 50, 20, 10],
  ZAR: [200, 100, 50, 20, 10, 5, 2, 1, 0.5],
  XOF: [10000, 5000, 2000, 1000, 500, 250, 200, 100, 50, 25],
  USD: [100, 50, 20, 10, 5, 1, 0.25, 0.1, 0.05, 0.01],
};

export function hasDenominations(currency: string) {
  return !!SETS[currency];
}

export default function DenominationCounter({
  currency = getCurrencyCode(),
  onTotal,
}: {
  currency?: string;
  /** Total in major units, to the cent. */
  onTotal: (major: number) => void;
}) {
  const values = SETS[currency] ?? SETS.GHS;
  const [counts, setCounts] = useState<Record<string, string>>({});
  // Sum in minor units so 0.1 + 0.2 doesn't drift.
  const totalMinor = useMemo(
    () => values.reduce((s, v) => s + Math.round(v * 100) * (Number(counts[String(v)]) || 0), 0),
    [counts, values],
  );
  useEffect(() => onTotal(totalMinor / 100), [totalMinor, onTotal]);

  return (
    <div className="rounded-lg border p-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {values.map((v) => (
          <label key={v} className="flex items-center gap-2 text-sm">
            <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">{v < 1 ? v.toFixed(2) : v.toLocaleString()}</span>
            <span className="text-muted-foreground">×</span>
            <Input
              inputMode="numeric"
              value={counts[String(v)] ?? ""}
              onChange={(e) => setCounts((c) => ({ ...c, [String(v)]: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
              placeholder="0"
              className="h-9 w-16 text-center tabular-nums"
              aria-label={`How many ${v} ${currency}`}
            />
          </label>
        ))}
      </div>
      <p className="mt-2 text-right text-sm">
        Counted: <span className="font-semibold tabular-nums">{formatMoney(totalMinor, currency)}</span>
      </p>
    </div>
  );
}
